// @flow

import _ from "underscore";
import type {IntegerType} from "./model";

/**
 * Mõõtejoone otspunkti klass
 */
class Endpoint {
    /**
     * X koordinaat
     */
    x: IntegerType;
    /**
     * Y koordinaat
     */
    y: IntegerType;
    /**
     * Z koordinaat
     */
    z: IntegerType;
    /**
     * Otspunkti mõõtmise kalle x teljel
     */
    slopeX: IntegerType;
    /**
     * Otspunkti mõõtmise kalle y teljel
     */
    slopeY: IntegerType;
    /**
     * Prindi kaugus klaasi servast
     */
    printOffset: IntegerType;

    constructor(params) {
        this.x = 0;
        this.y = 0;
        this.z = 0;
        this.slopeX = 0;
        this.slopeY = 0;
        this.printOffset = 0;

        if (params) _.extend(this, params);

    }
}

class Tolerance {
    /**
     * Tolerantsi nimi. Peab olema üks järgnevatest:
     *                  Ai - joone alguses olev nominaalpunkt (i on 1, 2, 3, ...)
     *                  Bi - joone keskel olev nominaalpunkt (i on 1, 2, 3, ...)
     *                  Ci - jooone lõpus olev nominaalpunkt (i on 1, 2, 3, ...)
     *                  Pi (i = 1 .. 9/15) - asendavad A, B, C, kus i = jooneNr*3 +1 (joone algses oleva punkti korral), +2 (joone keskel oleva punkti korral) või +3 (joone lõpus oleva punkti korral) ; kus joone Nr on 0 .. 2/4
     *                  Pj (j = 300 .. 50) - keskmise joone peal asuv peeglialuse ala nominaalpunkt, siin j on sammuga 50 ja näitab kaugust klaasi ülaservast
     *                  ANGLE - Nurk kahe valitud punkti vahel
     *                  Hi (i = 1 .. 5) - HUD punkt, järjestatud kasvavalt klaasi alaserva suhtes
     */
    name: string;
    /**
     * Nominaali ettenähtud mõõteväärtus
     */
    value: ?IntegerType;                       // Nominaali ettenähtud mõõteväärtus
    /**
     * ülemine 1. tolerants
     */
    upperTolerance1: ?IntegerType;	                    // ülemine 1. tolerants
    /**
     * alumine 1. tolerants
     */
    lowerTolerance1: ?IntegerType;	                    // alumine 1. tolerants
    /**
     * ülemine 2. tolerants
     */
    upperTolerance2: ?IntegerType;	                    // ülemine 2. tolerants
    /**
     * alumine 2. tolerants
     */
    lowerTolerance2: ?IntegerType;	                    // alumine 2. tolerants

    constructor(params) {
        this.name = "";
        this.value = null;
        this.upperTolerance1 = null;
        this.lowerTolerance1 = null;
        this.upperTolerance2 = null;
        this.lowerTolerance2 = null;

        if (params) _.extend(this, params);
    }
}

/**
 * Mõõtepunkti klass
 */
class NominalPoint extends Tolerance {
    /**
     * Joone number (1..3/5)
     * @type {IntegerType}
     */
    line: ?IntegerType;
    /**
     * Kaugus klaasi servast
     */
    distance: ?IntegerType;                  // Kaugus klaasi servast
    /**
     * Korrektsioon
     */
    correction: ?IntegerType;            // Korrektsioon
    /**
     * Kas on "ADAS akna punkt"
     */
    isAdas: ?boolean;
    /**
     * Kas punkt kuvatakse mõõtmise ekraanil
     */
    show: ?boolean;                 // Kas punkt kuvatakse mõõtmise ekraanil

    constructor(params) {
        super(null);

        this.distance = null;
        this.correction = null;
        this.isAdas = false;
        this.show = false;

        if (params) _.extend(this, params);
    }
}

/**
 * Nurga määramise klass
 */
class NominalAngle extends Tolerance {
    /**
     * First point name
     */
    point1: string;
    /**
     * Second point name
     */
    point2: string;

    constructor(params) {
        super(null);

        this.point1 = null;
        this.point2 = null;

        if (params) _.extend(this, params);
    }
}

/**
 * Mõõtejoone klass
 */
class MeasurementLine {
    /**
     * Mõõtejoone pikkus
     */
    lineLength: IntegerType;
    /**
     * Otspunktid, 2tk
     */
    endpoints: Array<Endpoint>;	// 2 tk
    /**
     * Nominaalid
     */
    nominals: Array<NominalPoint>;
    /**
     * Nurgad
     */
    angles: Array<NominalAngle>;

    constructor(params) {
        this.lineLength = 0;
        this.endpoints = [];	// 2 tk
        this.nominals = [];
        this.angles = [];

        if (params) _.extend(this, params);
    }
}

class AdasArea {
    /**
     * ADAS ala distants klaasi servast
     * @type {IntegerType}
     */
    distance: IntegerType;
    /**
     * ADAS ala tüüüp. Üks järgmistest:
     *  0 - Klaas
     *  1 - Print
     *  2 - Raster (ei kasutata hetkel)
     *  3 - Soojendusjuhe (ei kasutata hetkel)
     * @type {0|1|2|3}
     */
    type: 0 | 1 | 2 | 3;

    constructor(params) {
        this.distance = 0;
        this.type = 0;
        if (params) _.extend(this, params);
    }
}

/**
 * Universaalse JSON tootefaili esitus
 */
class GlassProduct {
    /**
     * Versioon
     */
    version: IntegerType;
    /**
     * Mõõtejoonte arv
     */
    lineCount: IntegerType;
    /**
     * Toote kood ja variant. Ei edastata PLCsse
     */
    codeVariant: string;
    /**
     * Toote SCAN kood. Ei edastata PLCsse
     */
    scanCode: string; // Toote SCAN kood. Ei edastata PLCsse
    /**
     * Toote nimetus (description). Ei edastata PLCsse
     */
    description: string; // Toote nimetus (description). Ei edastata PLCsse
    /**
     * Mõõtejooned
     */
    measurementLines: Array<MeasurementLine>; // Iga element vastab ühele mõõtejoonele, kuni viis mõõtejoont
    /**
     * Klaasi paksus
     */
    glassThickness: IntegerType;                          // Klaasi paksus
    /**
     * Lasersersensori mõõteprofiil klaasi jaoks
     */
    measuringProfileGlass: IntegerType;                           // Lasersersensori mõõteprofiil klaasi jaoks
    /**
     * Lasersersensori mõõteprofiil prindi jaoks
     */
    measuringProfilePrint: IntegerType;                           // Lasersersensori mõõteprofiil prindi jaoks
    /**
     * Klaasi joone max ja min x koordinaatide vahe. Ei edastata PLCsse
     */
    diffX: IntegerType;                                   // Klaasi joone max ja min x koordinaatide vahe. Ei edastata PLCsse
    /**
     * Klaasi joone max ja min y koordinaatide vahe. Ei edastata PLCsse
     */
    diffY: IntegerType;                                   // Klaasi joone max ja min y koordinaatide vahe. Ei edastata PLCsse
    /**
     * Klaasijoone nihutus x-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide x-väärtustele
     */
    offsetX: IntegerType;                                   // Klaasijoone nihutus x-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide x-väärtustele
    /**
     * Klaasijoone nihutus y-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide y-väärtustele
     */
    offsetY: IntegerType;                                   // Klaasijoone nihutus y-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide y-väärtustele
    /**
     * Distance
     * @type {IntegerType}
     */
    distance: IntegerType;
    /**
     * GR2 faili enihe parameeter. Ignoreeritakse, ei edastata PLCsse
     */
    offsetE: IntegerType;                                   // GR2 faili enihe parameeter. Ignoreeritakse, ei edastata PLCsse
    /**
     * Mõõtejoonte ülemise otsa X-koordinaadid. Ei edastata PLCsse
     */
    lineUpper: Array<IntegerType>;                               // Mõõtejoonte ülemised koordinaadid. Ei edastata PLCsse
    /**
     * Mõõtejoonte alumise osa X-koordinaadid. Ei edastata PLCsse
     */
    lineLower: Array<IntegerType>;                               // Mõõtejoonte alumised koordinaadid. Ei edastata PLCsse
    /**
     * Mõõtepunktide x-koordinaadid. Ei edastata PLCsse
     */
    pX: Array<IntegerType>;                                  // Mõõtepunktide x-koordinaadid. Ei edastata PLCsse
    /**
     * Mõõtepunktide y-koordinaadid. Ei edastata PLCsse
     */
    pY: Array<IntegerType>;                                  // Mõõtepunktide y-koordinaadid. Ei edastata PLCsse
    /**
     * Kas mõõtejoonte failis on mõõtejoon keeratud 180 kraadi
     */
    rotatedLine: boolean;                            // Kas mõõtejoonte failis on mõõtejoon keeratud
    /**
     * Keskmiste mõõtepunktide 1. tolerants
     */
    toleranceMiddle1: IntegerType;                             // Keskmiste mõõtepunktide 1. tolerants
    /**
     * Keskmiste mõõtepunktide 2. tolerants
     */
    toleranceMiddle2: IntegerType;                             // Keskmiste mõõtepunktide 2. tolerants
    /**
     * Serva mõõtepunktide 1. tolerants
     */
    toleranceEdge1: IntegerType;                             // Serva mõõtepunktide 1. tolerants
    /**
     * Serva mõõtepunktide 2. tolerants
     */
    toleranceEdge2: IntegerType;                             // Serva mõõtepunktide 2. tolerants
    /**
     * Kas mõõtejoon tuleb esitada peegeldatuna
     */
    mirrored: boolean;                         // Kas mõõtejoon tuleb esitada peegeldatuna
    /**
     * Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
     */
    rotatedFirst: boolean;                    // Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
    /**
     * Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
     */
    rotatedLast: boolean;                    // Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
    /**
     * Kas on keeratud 90 kraadi
     */
    rotated: boolean;

    /**
     * ADAS alade kirjeldused (kuni 6 ala)
     * @type {Array<AdasArea>}
     */
    adasAreas: Array<AdasArea>;


    constructor(params) {
        this.version = 3;
        this.lineCount = 3;
        this.codeVariant = "";
        this.scanCode = ""; // Toote SCAN kood. Ei edastata PLCsse
        this.description = ""; // Toote nimetus (description). Ei edastata PLCsse
        this.measurementLines = []; // Iga element vastab ühele mõõtejoonele, kuni viis mõõtejoont
        this.glassThickness = 0;                          // Klaasi paksus
        this.measuringProfileGlass = 0;                           // Lasersersensori mõõteprofiil klaasi jaoks
        this.measuringProfilePrint = 0;                           // Lasersersensori mõõteprofiil prindi jaoks
        this.diffX = 0;                                   // Klaasi joone max ja min x koordinaatide vahe. Ei edastata PLCsse
        this.diffY = 0;                                   // Klaasi joone max ja min y koordinaatide vahe. Ei edastata PLCsse
        this.offsetX = 0;                                   // Klaasijoone nihutus x-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide x-väärtustele
        this.offsetY = 0;                                   // Klaasijoone nihutus y-teljel. Nihkeväärtus liidetakse juurde kõikide otspunktide y-väärtustele
        this.offsetE = 0;                                   // GR2 faili enihe parameeter. Ignoreeritakse, ei edastata PLCsse
        this.distance = 0;
        this.lineUpper = [];                               // Mõõtejoonte ülemised koordinaadid. Ei edastata PLCsse
        this.lineLower = [];                               // Mõõtejoonte alumised koordinaadid. Ei edastata PLCsse
        this.pX = [];                                  // Mõõtepunktide x-koordinaadid. Ei edastata PLCsse
        this.pY = [];                                  // Mõõtepunktide y-koordinaadid. Ei edastata PLCsse
        this.rotatedLine = false;                            // Kas mõõtejoonte failis on mõõtejoon keeratud
        this.toleranceMiddle1 = 0;                             // Keskmiste mõõtepunktide 1. tolerants
        this.toleranceMiddle2 = 0;                             // Keskmiste mõõtepunktide 2. tolerants
        this.toleranceEdge1 = 0;                             // Serva mõõtepunktide 1. tolerants
        this.toleranceEdge2 = 0;                             // Serva mõõtepunktide 2. tolerants
        this.mirrored = false;                         // Kas mõõtejoon tuleb esitada peegeldatuna
        this.rotatedFirst = false;                    // Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
        this.rotatedLast = false;                    // Edastatakse PLCsse, kui konfiguratsioonis on "rotated_measurements_allowed" tõene
        this.rotated = false;
        this.adasAreas = [];

        if (params) _.extend(this, params);
    }

}

export {GlassProduct as default, MeasurementLine, Endpoint, Tolerance, NominalPoint, NominalAngle, AdasArea};
